<template>
  <div
      v-if="isOpen"
      :id="id"
      ref="overlay"
      class="mfa-overlay"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="`${id}-title`"
      :aria-describedby="`${id}-description`"
  >
    <div class="mfa-backdrop" />

    <div class="mfa-dialog">
      <!-- Header -->
      <div class="mfa-header">
        <div class="mfa-header-container">
          <button
              v-if="canGoBack"
              class="mfa-header-btn mfa-header-back"
              @click="goBack"
              aria-label="Back"
          >
            <i class="fa-regular fa-angle-left"></i>
          </button>
          <h2 :id="`${id}-title`" class="mfa-title">{{ headerTitle }}</h2>
        </div>
        <button type="button" class="mfa-close" aria-label="Close dialog" @click="close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <transition name="mfa-view" mode="out-in" appear @after-enter="afterEntered" @after-appear="afterEntered">
      <!-- View: code -->
        <div v-if="currentView === 'code'" class="mfa-body">
          <p :id="`${id}-description`" class="mfa-description">{{ description }}</p>

          <form class="mfa-form" novalidate @submit.prevent="submit">
            <div class="mfa-field">
              <label class="mfa-label" :for="`${id}-input`">Verification code</label>
              <input
                  v-model="code"
                  ref="codeInput"
                  class="mfa-input"
                  :class="{ 'is-error': hasError }"
                  type="text"
                  inputmode="text"
                  pattern="[A-Z0-9]"
                  :maxlength="codeLength"
                  autocomplete="off"
                  :aria-label="`Code input`"
                  :id="`${id}-input`"
                  @input="onInput()"
              />
            </div>
            <transition name="mfa-error-fade">
              <div v-if="errorMessage" class="mfa-error" role="alert">
                <i class="fa-regular fa-circle-exclamation"></i>
                {{ errorMessage }}
              </div>
            </transition>

            <p v-if="expiryText" class="mfa-description">{{ expiryText }}</p>
          </form>
        </div>

        <!-- View: method-picker -->
        <div v-else-if="currentView === 'method-picker'" key="picker" class="mfa-body">
          <p v-if="isSetup" class="mfa-subtext mfa-body-text">
            Your account admin is requiring two-factor authentication (2FA) on your account<template v-if="graceDays > 1">, you have {{ graceDays }} days remaining</template><template v-else-if="graceDays === 1">, you have 1 day remaining</template>. Please sign up using one of the methods below.
            <a class="mfa-body-link" target="_blank" href="https://blogs.gadzoom.net/2026/06/15/2fa-login-methods/">Learn more here.</a>
          </p>
          <p v-else class="mfa-subtext mfa-body-text">Select a verification method and we'll send a code.</p>

          <div class="mfa-method-list">
            <button
                v-for="opt in selectableMethods"
                :key="opt.method"
                class="mfa-method-item"
                :class="{ 'is-current': opt.method === challengeMethod, 'is-loading': resendLoading && pendingMethod === opt.method }"
                type="button"
                :disabled="resendLoading"
                @click="selectMethod(opt)"
            >
              <span class="mfa-method-icon-wrap">
                <i v-if="opt.method === 'TOTP'" class="fa-regular fa-mobile-notch"/>
                <i v-else-if="opt.method === 'SMS'" class="fa-regular fa-comment"/>
                <i v-else class="fa-regular fa-envelope"/>
              </span>
              <span class="mfa-method-info">
                <span class="mfa-method-name">{{ methodLabel(opt.method) }}</span>
                <template v-if="isSetup">
                  <span v-if="opt.method === 'SMS'" class="mfa-method-dest">Use your phone number for two-factor authentication. Enter the code sent via SMS.</span>
                  <span v-else-if="opt.method === 'EMAIL'" class="mfa-method-dest">Use your email for two-factor authentication. Enter the code sent to your account's email.</span>
                  <span v-else-if="opt.method === 'TOTP'" class="mfa-method-dest">Use your authenticator app for authentication. Enter the code it provides when prompted.</span>
                </template>
                <template v-else>
                  <span v-if="opt.challengeDestination" class="mfa-method-dest">{{ opt.challengeDestination }}</span>
                </template>
              </span>
              <span v-if="!isSetup" class="mfa-method-security">
                {{ methodSecurityLabel(opt.method) }}
                <span v-if="resendLoading && pendingMethod === opt.method" class="mfa-spinner"></span>
                <i v-else-if="opt.method === challengeMethod" class="fa-regular fa-check"/>
              </span>
            </button>
          </div>

          <transition name="mfa-error-fade">
            <div v-if="errorMessage" class="mfa-error" role="alert">
              <i class="fa-regular fa-circle-exclamation"></i>
              {{ errorMessage }}
            </div>
          </transition>

          <button v-if="!isSetup" class="mfa-link mfa-backup-link" type="button" @click="openBackupCode">
            Use a backup code
          </button>
          <button v-else-if="graceDays && graceDays > 0" class="mfa-link mfa-backup-link" type="button" @click="emitConfirmed">
            Skip
          </button>
        </div>

        <!-- View: Backup Code -->
        <div v-else-if="currentView === 'backup-code'" key="backup-code" class="mfa-body">
          <span class="mfa-subtext mfa-body-text">
            Enter one of your single-use backup codes
          </span>

          <div class="mfa-field">
            <label class="mfa-label" for="mfa-backup-code">Backup Code</label>
            <input
                id="mfa-backup-code"
                type="text"
                ref="backupInput"
                v-model="backupCode"
                class="mfa-input"
                autocomplete="off"
                spellcheck="false"
                @input="onInput()"
                @keydown.enter="submit"
            />
          </div>

          <transition name="mfa-error-fade">
            <div v-if="errorMessage" class="mfa-error" role="alert">
              <i class="fa-regular fa-circle-exclamation"></i>
              {{ errorMessage }}
            </div>
          </transition>
        </div>

        <!-- View: Backup Code List -->
        <div v-else-if="currentView === 'backup-list'" key="backup-list" class="mfa-body">
          <h3 class="mfa-heading">Save your backup codes</h3>
          <span class="mfa-subtext">
                Each code can only be used once if you lose access to your two-factor authentication method. Make sure to copy and save them in a safe place.
              </span>

          <div class="codes-wrapper">
            <div class="mfa-backup-list">
              <button class="copy-btn" @click="copyCodes">
                <transition name="tooltip">
                  <span v-if="showCopied" class="copy-tooltip">Copied!</span>
                </transition>
                <i class="fa-regular fa-copy"/>
              </button>
              <span v-for="code in backupList" :key="code">{{ code }}</span>
            </div>
          </div>

          <button
              class="mfa-link mfa-diff-method-link"
              type="button"
              :disabled="downloading"
              @click="downloadCodes"
          >
            Download
          </button>

          <transition name="mfa-error-fade">
            <div v-if="errorMessage" class="mfa-error" role="alert">
              <i class="fa-regular fa-circle-exclamation"></i>
              {{ errorMessage }}
            </div>
          </transition>
        </div>

        <!--Edit view-->
        <div v-else-if="currentView === 'edit-method'" key="edit-method" class="mfa-body">
          <div class="mfa-field">
            <label class="mfa-label" for="mfa-activate-destination">{{ methodLabel(challengeMethod) }} for Verification</label>
            <div v-if="challengeMethod === 'SMS'" class="ui-library redesigned fullscreen">
              <vue-tel-input
                  id="mfa-activate-destination"
                  ref="activateDestination"
                  v-model="editDestination"
                  :inputOptions="telInputOptions"
              >

              </vue-tel-input>
            </div>
            <input
                v-else
                id="mfa-activate-destination"
                ref="activateDestination"
                type="text"
                v-model="editDestination"
                :placeholder="editPlaceholder"
                class="mfa-input"
                autocomplete="off"
                spellcheck="false"
                @keydown.enter="submit"
            />
          </div>

          <transition name="mfa-error-fade">
            <div v-if="errorMessage" class="mfa-error" role="alert">
              <i class="fa-regular fa-circle-exclamation"></i>
              {{ errorMessage }}
            </div>
          </transition>
        </div>

        <!--Activate view-->
        <div v-else-if="currentView === 'activate-method'" key="activate-method" class="mfa-body">
          <span class="mfa-activate-body">
            <template v-if="challengeMethod === 'SMS'">
              <i class="fa-regular fa-comment"/>
              <span>
                A text message with your code has been sent to {{editDestination.length > 0 ? editDestination : editPlaceholder}}
              </span>
            </template>
            <template v-else>
              <i class="fa-regular fa-envelope"/>
              <span>
                An email with your code has been sent to {{editDestination.length > 0 ? editDestination : editPlaceholder }}
              </span>
            </template>
          </span>

          <div class="mfa-field">
            <label class="mfa-label" for="mfa-activate-code">Enter Code</label>
            <input
                id="mfa-activate-code"
                type="text"
                ref="activateCodeInput"
                v-model="code"
                class="mfa-input"
                autocomplete="off"
                spellcheck="false"
                @keydown.enter="submit"
            />
          </div>

          <transition name="mfa-error-fade">
            <div v-if="errorMessage" class="mfa-error" role="alert">
              <i class="fa-regular fa-circle-exclamation"></i>
              {{ errorMessage }}
            </div>
          </transition>
        </div>

        <!--Activate TOTP view-->
        <div v-else-if="currentView === 'activate-totp'" key="activate-totp" class="mfa-body">
          <div class="mfa-activate-body">
            <div class="step-number">1</div>
            <div>
              <span>
                Download TOTP on your phone
              </span>
              <br>
              <br>
              <a target="_blank" href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_US">Download on Google Play</a>
              <br>
              <a target="_blank" href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458">Download on the iOS App Store</a>
            </div>
          </div>

          <div class="mfa-activate-body">
            <div class="step-number">2</div>
            <div>
              Open the app and scan this QR code or enter the key
              <br>
              <br>
              <span v-if="generating" class="is-loading">Loading…</span>
              <span v-else class="mfa-subtext">{{ totpSecretKey }}</span>
            </div>
          </div>

          <div class="mfa-qr-image">
            <div v-if="generating" class="qr-skeleton" />
            <div v-else-if="!totpQrUrl" class="qr-error">
              <i class="fa-regular fa-rotate-right"></i>
              <button class="mfa-link" @click="launchTotp">Retry</button>
            </div>
            <img v-else :src="`data:image/png;base64,${totpQrUrl}`" alt="QR Code"/>
          </div>

          <div class="mfa-activate-body">
            <div class="step-number">3</div>
            <div>
              <span>Enter the verification code from the app on this page</span>
            </div>
          </div>

          <div class="mfa-field">
            <label class="mfa-label" for="mfa-activate-code">Enter Code</label>
            <input
                id="mfa-activate-code"
                type="text"
                v-model="code"
                ref="activateCodeInput"
                class="mfa-input"
                autocomplete="off"
                spellcheck="false"
                @keydown.enter="submit"
            />
          </div>

          <transition name="mfa-error-fade">
            <div v-if="errorMessage" class="mfa-error" role="alert">
              <i class="fa-regular fa-circle-exclamation"></i>
              {{ errorMessage }}
            </div>
          </transition>
        </div>
      </transition>

      <!-- Footer -->
      <div v-if="currentView !== 'method-picker'" class="mfa-footer">
        <button
            v-if="showBackupCodeButton"
            type="button"
            class="mfa-link mfa-footer-backup-link"
            @click="openBackupCode"
        >
          Use Backup Code
        </button>
        <button
            v-if="showResend"
            type="button"
            class="mfa-btn mfa-btn--resend"
            :class="{ 'is-loading': resendLoading }"
            :disabled="resendLoading || resendBlocked"
            @click="resendCode"
        >
          {{ resendBlocked ? `Resend Code (${resendCountdown}s)` : 'Resend Code' }}
        </button>
        <button
            type="button"
            class="mfa-btn mfa-btn--primary"
            :class="{ 'is-loading': isLoading }"
            :disabled="isLoading || !canSubmit"
            :title="submitTitle"
            @click="submit"
        >
          {{ submitText }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import {downloadBackupCodesPdf} from './PdfGenerator'

import {MFA_METHODS} from "./mfaMethods";

const generic_descriptions = {
  [MFA_METHODS.TOTP]:  'generated by your Authenticator App',
  [MFA_METHODS.EMAIL]: 'sent to your email',
  [MFA_METHODS.SMS]: 'sent to your phone',
};

/**
 * Emit 'confirmed' with the server response.
 * Callers use this to chain to the next screen/action:
 *
 * <mfa-modal
 *   :show="showMfa"
 *   action-type="mfa.activate"
 *   :available-methods="mfaMethods"
 *   @confirmed="onMfaConfirmed"
 *   @close="showMfa = false"
 * />
 *
 * methods: {
 *   onMfaConfirmed(response) {
 *     this.showMfa = false;
 *     // ...Next action
 *   }
 * }
 */
export default {
  name: 'MfaModal',

  props: {
    /**
     * Used to determine id names of elements
     */
    id: {
      type: String,
      default: 'mfa-modal',
    },
    /**
     * List of method objects from /api/v1/mfa/options response.
     * Shape: { method: 'TOTP'|'SMS'|'EMAIL', destination: string|null, activated: boolean, codeLength: number }
     */
    mfaOptions: {
      type: Array,
      default: [],
    },
    /**
     * Control visibility from the parent via v-model
     */
    value: {
      type: Boolean,
      default: false,
    },
    /**
     * The action being guarded (e.g. 'mfa.activate', 'mfa.validate')
     */
    actionType: {
      type: String,
      default: null,
    },
    /**
     * The action being guarded (e.g. 'mfa.activate', 'mfa.validate')
     */
    graceDays: {
      type: Number,
      default: null,
    }
  },

  data() {
    return {
      currentView: 'code',
      rawCode: '',
      rawBackupCode: '',
      challengeMethod: null,
      pendingMethod: null,
      challengeDestination: null,
      editDestination: '',
      editPlaceholder: '',
      errorMessage: '',
      isLoading: false,
      resendLoading: false,
      resendCountdown: 0,

      backupList: [],
      downloading: false,
      copied: false,
      showCopied: false,
      regenerating: false,

      generating: false,
      totpSecretKey: null,
      totpQrUrl: null,
    };
  },

  computed: {
    code: {
      get() { return this.rawCode; },
      set(v) { this.rawCode = v.replace(/[^A-Z0-9]/gi, '').toUpperCase(); },
    },
    backupCode: {
      get() { return this.rawBackupCode; },
      set(v) { this.rawBackupCode = v.replace(/[^A-Z0-9]/gi, '').toUpperCase(); },
    },

    telInputOptions() {
      return {
        placeholder: this.editPlaceholder,
        type: 'tel',
        autocomplete: 'on',
      };
    },

    submitText() {
      if (this.submitting) return 'Confirming…';
      else if (this.currentView === 'edit-method') {
        if (!this.editDestination || this.editDestination.length === 0) return `Use Default ${this.methodReadable(this.challengeMethod)}`
        return 'Send Verification Code';
      }
      else if (this.currentView === 'activate-method' || this.currentView === 'activate-totp') return 'Verify';
      else return 'Confirm';
    },

    submitTitle() {
      if (this.submitting || this.canSubmit) {
        return '';
      } else if (this.currentView === 'backup-list') {
        return 'Please copy backup codes';
      } else if (this.currentView === 'activate-method' || this.currentView === 'activate-totp') {
        return 'Please enter code';
      } else if (this.currentView === 'edit-method') {
        return `Please enter ${this.methodReadable(this.challengeMethod)}`
      }

      const missing = this.password ? 'code' : 'password'
      return `Please enter ${missing}`
    },

    headerTitle() {
      switch (this.currentView) {
        case 'edit-method': {
          return `New Verification ${this.methodReadable(this.challengeMethod)}`
        }
        case 'activate-totp':
          // intentional fallthrough
        case 'activate-method': {
          return `${this.methodLabel(this.challengeMethod)} Verification`
        }
        case 'code': {
          return 'Enter Verification Code'
        }
        default: return 'Two-Factor Authentication'
      }
    },

    description() {
      if (this.challengeMethod === MFA_METHODS.TOTP) {
        return 'Enter the verification code generated by your Authenticator App'
      }

      if (this.challengeDestination !== null && this.challengeDestination !== undefined && this.challengeDestination.length > 0) {
        let prefix = this.challengeMethod === MFA_METHODS.EMAIL
            ? 'An email'
            : this.challengeMethod === MFA_METHODS.SMS ? 'A text message' : 'A message'
        return `${prefix} with your code has been sent to ${this.challengeDestination}`
      }

      const suffix = generic_descriptions[this.challengeMethod];
      if (suffix == null) {
        return `Enter your verification code`
      }
      return `Enter the verification code ${suffix}`;
    },

    expiryText() {
      return this.challengeMethod !== MFA_METHODS.TOTP
          ? 'This code expires in 10 minutes'
          : null;
    },

    codeLength() {
      const match = this.mfaOptions.find(m => m.method === this.challengeMethod);
      return match ? match.codeLength : 6;
    },

    selectableMethods() {
      const activated = !this.isSetup;
      return this.mfaOptions.filter(option => option.activated === activated);
    },

    canGoBack() {
      return ['backup-code', 'activate-method'].includes(this.currentView)
          || (['activate-totp', 'edit-method'].includes(this.currentView))
          || (this.currentView === 'code' && this.activatedMethods.length > 1)
    },

    canSubmit() {
      if (this.currentView === 'code') return this.code.length === this.codeLength;
      if (this.currentView === 'backup-code') return this.backupCode.length >= 8;
      if (this.currentView === 'backup-list') return this.copied;
      if (this.currentView === 'edit-method') return (this.editDestination && this.editDestination.trim().length > 0) || this.editPlaceholder.length > 0;
      if (this.currentView === 'activate-method' || this.currentView === 'activate-totp') return this.code.length === this.codeLength;
      return false;
    },

    showResend() {
      return (this.currentView === 'code' && this.challengeMethod !== MFA_METHODS.TOTP) || this.currentView === 'activate-method';
    },

    resendBlocked() {
      return this.resendCountdown > 0;
    },

    isSetup() {
      return this.actionType === 'mfa.activate'
    },

    isOpen() {
      return this.value;
    },

    hasError() {
      return this.errorMessage && this.errorMessage.length > 0;
    },

    activatedMethods() {
      return this.mfaOptions.filter(option => option.activated);
    },

    showBackupCodeButton() {
      return this.currentView === 'code' && !this.isSetup && this.activatedMethods.length === 1;
    },
  },

  watch: {
    // Focus input when the modal opens
    isOpen(val) {
      if (!val) {
        document.removeEventListener('focusin', this.keepFocusInside, true);
        return;
      }
      this.reset();

      const activated = this.activatedMethods;

      // Guard focus while the dialog is open. The login <form> stays in the
      // DOM behind this overlay, and Safari re-runs credential autofill after
      // the XHR submit, focusing #username and opening the keychain dropdown.
      // A single .focus() call just loses that race; pull focus back instead.
      document.addEventListener('focusin', this.keepFocusInside, true);

      if (activated.length > 1 || activated.length === 0) {
        this.currentView = 'method-picker';
        this.$nextTick(() => this.focusCurrentView());
        return;
      }

      this.challengeMethod = activated[0].method;
      this.challengeDestination = activated[0].destination;

      if (this.showResend) {
        this.resendCode();
      }

      // Single-method users never swap views, so the code input's focus would
      // otherwise ride solely on the <transition> @after-appear hook, whose
      // timing depends on when the (prod-extracted) CSS is applied.
      this.$nextTick(() => this.focusCurrentView());
    },
  },

  mounted() {
    document.addEventListener('keydown', this.onEscape);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onEscape);
    document.removeEventListener('focusin', this.keepFocusInside, true);
  },

  methods: {
    // ── Networking ────────────────────────────────────────────────────────

    async tryPost(endpoint, bodyObj, onSuccess, onError) {
      try {
        const res = await fetch(`/api/v1/mfa${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(bodyObj),
        });

        const data = await res.json().catch(() => ({}));
        if (data.code === 401) {
          this.close()
          return;
        }

        if (data.code === 403) {
          this.challengeId = null;
          this.challengeExpiresAt = null;
          this.currentView = 'code';
          this.password = '';
          this.code = '';

          onError(new Error('Your verification session has expired. Please re-enter your credentials.'))
        }

        if (res.ok && (!data.code || data.code === 200)) {
          onSuccess(data)
        } else {
          onError(new Error(data.msg));
        }
      } catch (e) {
        onError(e)
      }
    },

    // ── Resend ────────────────────────────────────────────────────────

    startResendCountdown() {
      this.resendCountdown = 30;
      const timer = setInterval(() => {
        this.resendCountdown--;
        if (this.resendCountdown <= 0) clearInterval(timer);
      }, 1000);
    },

    async resendCode() {
      if (this.resendBlocked || this.resendLoading) {
        return;
      }
      this.resendLoading = true;
      this.error = null;

      const isEditView = ['edit-method', 'activate-method'].includes(this.currentView);
      const url = isEditView ? '/add-option' : '/request-code';
      await this.tryPost(url, {
        method: this.pendingMethod || this.challengeMethod,
        destination: (this.editDestination && this.editDestination.length > 0) ? this.editDestination : null,
      }, () => {
        this.startResendCountdown();
      }, (err) => {
        this.errorMessage = (err && err.message) || 'Failed to request Two-Factor Authentication code';
      })

      this.resendLoading = false;
    },

    // ── Navigation ────────────────────────────────────────────────────────

    reset() {
      this.currentView = 'code';
      this.rawCode = '';
      this.rawBackupCode = '';
      this.challengeMethod = null;
      this.pendingMethod = null;
      this.challengeDestination = null;
      this.editDestination = '';
      this.editPlaceholder = '';
      this.errorMessage = '';
      this.isLoading = false;
      this.resendLoading = false;
      this.resendCountdown = 0;

      this.backupList = [];
      this.downloading = false;
      this.copied = false;
      this.showCopied = false;

      this.generating = false;
      this.totpSecretKey = null;
      this.totpQrUrl = null;
    },

    close() {
      this.$emit('close');
    },

    emitConfirmed() {
      this.$emit('verified');
    },

    onEscape(e) {
      if (e.key === 'Escape' && this.isOpen) this.close();
    },

    afterEntered() {
      this.focusCurrentView();
    },

    // Pull focus back into the dialog if something outside it (Safari autofill,
    // a stray Tab) steals it while the modal is open.
    keepFocusInside(e) {
      if (!this.isOpen) return;
      const overlay = this.$refs.overlay;
      if (overlay && !overlay.contains(e.target)) {
        this.focusCurrentView();
      }
    },

    focusCurrentView(attempt = 0) {
      if (!this.isOpen) return;

      const focusMap = {
        'code':            () => this.$refs.codeInput,
        'backup-code':     () => this.$refs.backupInput,
        'edit-method':     () => this.$refs.activateDestination,
        'activate-method': () => this.$refs.activateCodeInput,
        'activate-totp':   () => this.$refs.activateCodeInput,
      };

      const getRef = focusMap[this.currentView];
      if (!getRef) return;

      const ref = getRef();
      // vue-tel-input exposes focus() on the component instance,
      // native inputs have it on the element directly
      const el = ref && ref.$el ? ref.$el : ref;
      if (el && typeof el.focus === 'function') {
        if (document.activeElement !== el) el.focus();
      } else if (attempt < 5) {
        // ref not mounted yet (view still transitioning in) - retry next frame
        requestAnimationFrame(() => this.focusCurrentView(attempt + 1));
      }
    },

    goBack() {
      this.errorMessage = null;

      switch (this.currentView) {
        case 'activate-method':
          this.currentView = 'edit-method';
          break;
        case 'backup-code':
          if (this.activatedMethods.length === 1) {
            this.currentView = 'code';
          } else {
            this.challengeMethod = null;
            this.openMethodPicker();
          }
          break;
        default:
          this.challengeMethod = null;
          this.openMethodPicker();
          break;
      }
    },

    openMethodPicker() {
      this.error = null;
      this.currentView = 'method-picker';
    },

    openBackupCode() {
      this.error = null;
      this.currentView = 'backup-code';
    },

    methodLabel(method) {
      return { TOTP: 'Authenticator App', SMS: 'SMS', EMAIL: 'Email' }[method] || method;
    },

    methodReadable(method) {
      return { TOTP: 'authenticator app', SMS: 'phone number', EMAIL: 'email' }[method] || method;
    },

    methodSecurityLabel(method) {
      return { TOTP: 'Most secure', SMS: 'Less secure', EMAIL: 'Least secure' }[method] || '';
    },

    showError(message) {
      this.errorMessage = message;
      this.isLoading = false;
      this.afterEntered();
    },

    onInput() {
      this.clearError();

      if (this.canSubmit && this.currentView !== 'backup-code') {
        this.submitCode();
      }
    },

    clearError() {
      this.errorMessage = '';
    },

    setLoading(val) {
      this.isLoading = val;
    },

    // ── Submit ────────────────────────────────────────────────────────────

    async submit() {
      switch (this.currentView) {
        case 'backup-list':
          this.emitConfirmed();
          break;
        case 'edit-method':
          this.submitting = true;
          await this.resendCode();
          this.submitting = false;
          this.currentView = 'activate-method';
          break;
        default:
          await this.submitCode();
          break;
      }
    },

    async submitCode() {
      if (this.isLoading) return;

      if (!this.canSubmit) {
        this.showError('Please enter your code before verifying.');
        return;
      }

      this.setLoading(true);

      const isBackup = this.currentView === 'backup-code';
      const isEditView = ['activate-method', 'activate-totp'].includes(this.currentView);
      const url = `${isBackup ? '/backup-code' : (isEditView ? '/activate' : '/validate')}`
      await this.tryPost(url, { method: this.challengeMethod, code: isBackup ? this.backupCode : this.code }, (data) => {
        if (data.sessionId != null) {
          if (data.backupCodes && data.backupCodes.length > 0) {
            this.currentView = 'backup-list';
            this.backupList = data.backupCodes;
            this.setLoading(false);
          } else {
            this.emitConfirmed();
          }
        } else {
          this.showError('Please check your code and try again');
        }
      }, (err) => {
        this.showError((err && err.message) || 'Something went wrong. Please try again.');
      })
    },

    async launchTotp() {
      this.currentView = 'activate-totp';
      this.generating = true;
      await this.tryPost('/generate-totp', { includeQr: true }, (data) => {
        this.totpSecretKey = data.secretKey;
        this.totpQrUrl = data.qrImage;
      }, (err) => {
        this.error = (err && err.message) || 'Failed to generate Secret Key';
      })
      this.generating = false;
    },

    startActivateFlow() {
      if (this.challengeMethod === null) {
        this.openMethodPicker();
        return;
      } else if (this.challengeMethod === 'TOTP') {
        this.launchTotp();
        return;
      }

      this.currentView = 'edit-method';
      const match = this.selectableMethods.find(m => m.method === this.challengeMethod);

      this.editPlaceholder = (match && match.challengeDestination) || '';
      this.editDestination = '';
    },

    // ── Method switching ──────────────────────────────────────────────────

    async selectMethod(opt) {
      if (this.activatedMethods.length === 0) {
        this.code = '';
        this.error = null;
        this.challengeMethod = opt.method;
        this.challengeDestination = opt.challengeDestination;
        this.startActivateFlow();

        return;
      }

      if (opt.method === this.challengeMethod) {
        this.currentView = 'code';
        return;
      }

      // TOTP generates its own codes; no request-code call needed
      if (opt.method !== 'TOTP') {
        this.pendingMethod = opt.method;

        await this.resendCode();

        this.pendingMethod = null;
      }

      this.code = '';
      this.error = null;
      this.challengeMethod = opt.method;
      this.challengeDestination = opt.challengeDestination;
      this.editDestination = '';
      this.currentView = 'code';
    },

    // ── Backup codes ────────────────────────────────────────────────────────────

    async downloadCodes() {
      this.downloading = true;

      downloadBackupCodesPdf(this.backupList)

      // Successfully downloaded
      this.copied = true;
      this.downloading = false;
    },

    copyCodes() {
      navigator.clipboard.writeText(this.backupList.join('\n'));
      this.copied = true;
      this.showCopied = true;
      setTimeout(() => this.showCopied = false, 2000);
    },
  },
};
</script>

<style scoped lang="scss">

.mfa-overlay {
  --mfa-text:              #323246;
  --mfa-text-body:         #161F28;
  --mfa-action:            #1A3149;
  --mfa-secondary:         #2271A5;
  --mfa-secondary-light:   #2271A559;
  --mfa-secondary-hover:   #1e3a5f0f;
  --mfa-error-color:       #dc2626;
  --mfa-transition:        160ms cubic-bezier(0.4, 0, 0.2, 1);
}

.mfa-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mfa-overlay .mfa-dialog {
  animation: mfa-slide-in var(--mfa-transition) both;
}

.mfa-overlay .mfa-backdrop {
  animation: mfa-fade-in 120ms ease both;
}

@keyframes mfa-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

@keyframes mfa-slide-in {
  from { opacity: 0; transform: translateY(-8px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0)    scale(1);    }
}

.mfa-backdrop {
  position: fixed;
  inset: 0;
  background: #00000099;
  -ms-filter: "alpha(opacity=60)";
  backdrop-filter: blur(2px);
}

.mfa-dialog {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 520px;
  background: #FFF;
  border-radius: 4px;
  box-shadow: 0 8px 40px #0f172a2e, 0 2px 8px #0f172a14;
  display: flex;
  flex-direction: column;
}

.mfa-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 12px 16px;
  border-bottom: 1px solid #EEEEF4;
}

.mfa-header-container {
  display: flex;
}

.mfa-header-btn.mfa-header-back {
  font-size: 16px;
  min-width: 20px;
  min-height: 20px;
}

.mfa-title {
  margin: 0;
  line-height: 24px;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0;
  color: var(--mfa-text);
}

.mfa-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--mfa-text);
  cursor: pointer;
  transition: background var(--mfa-transition), color var(--mfa-transition);
  flex-shrink: 0;
}

.mfa-close:hover { background: #f1f5f9; color: var(--mfa-text); }
.mfa-close:focus-visible { outline: 2px solid var(--mfa-action); outline-offset: 2px; }

.mfa-body {
  padding: 16px 24px 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mfa-description {
  margin: 0;
  font-size: 16px;
  line-height: 20px;
  font-weight: 400;
  color: var(--mfa-text-body);
}
.mfa-subtext {
  font-size: 16px;
  font-weight: 400;
  color: #707070;
  line-height: 24px;
}
  /*margin: 0 0 24px;*/

.mfa-subtext.mfa-body-text {
  color: #161F28;
}

.mfa-body-link {
  color: #2271A5;
  text-decoration: underline !important;
}

.step-number {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #2271A5;
  color: #fff;
  font-weight: 700;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mfa-form { display: contents; }

.mfa-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mfa-label {
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  color: var(--mfa-text-body);
}

.mfa-field .mfa-form .mfa-input {
  border: 1px solid #DFE0E3;
  border-radius: 8px;
  color: #172530;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 24px;
  font-size: 16px;
  transition: border-color var(--mfa-transition), box-shadow var(--mfa-transition), background var(--mfa-transition);
  -moz-appearance: textfield;
  appearance: textfield;
}

.mfa-field .mfa-form .mfa-input:focus {
  outline: none;
  border-color: var(--mfa-action);
  box-shadow:  0 0 0 3px rgba(30, 58, 95, 0.12);
}

.mfa-field .mfa-form .mfa-input.is-error  { border-color: var(--mfa-error-color); box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12); }

.mfa-error {
  margin: 0 0 12px;
  padding: 8px 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: var(--mfa-error-color);
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
}

.mfa-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 24px;
  background: #F7F7FA;
}

.mfa-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background var(--mfa-transition), border-color var(--mfa-transition), color var(--mfa-transition);
  white-space: nowrap;
}

.mfa-btn:focus-visible { outline: 2px solid var(--mfa-action); outline-offset: 2px; }

.mfa-btn--resend {
  background: transparent;
  border: 1.5px solid var(--mfa-secondary);
  color: var(--mfa-secondary);
}
.mfa-btn--resend:hover { background: var(--mfa-secondary-hover); }
.mfa-btn--resend:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.mfa-btn--resend.is-loading { pointer-events: none; opacity: 0.75; }
.mfa-btn--resend.is-loading::after {
  border: 2px solid var(--mfa-secondary-light);
  border-top-color: var(--mfa-secondary);
}

.mfa-btn--primary {
  background: var(--mfa-action);
  border: 1.5px solid transparent;
  color: #FFF;
}
.mfa-btn--primary:hover  { background: var(--mfa-secondary); }
.mfa-btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--mfa-action);
}
.mfa-btn--primary.is-loading { pointer-events: none; opacity: 0.75; }
.mfa-btn--primary.is-loading::after {
  border: 2px solid #FFFFFF59;
  border-top-color: #FFF;
}

.is-loading::after {
  content: '';
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-left: 8px;
  border: 2px solid #2271A559;
  border-top-color: #2271A5;
  border-radius: 50%;
  animation: mfa-loading-spin 0.7s linear infinite;
}

@keyframes mfa-loading-spin { to { transform: rotate(360deg); } }

/* ── Links ────────────────────────────────────────────────────────────────── */
.mfa-link {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: #2271A5;
  padding: 0;
  text-decoration: none;
  transition: color 0.15s;
  border-radius: 2px;
}
.mfa-link:hover:not(:disabled) { color: #e5bc48; text-decoration: underline; }
.mfa-link:disabled { opacity: 0.5; cursor: default; text-decoration: none; }
.mfa-link:focus-visible { outline: 2px solid -webkit-focus-ring-color; outline-offset: 2px; }

.mfa-diff-method-link {
  margin-top: 12px;
  align-self: center;
}

.mfa-backup-link {
  margin: 4px 0 8px;
}

.mfa-footer-backup-link {
  margin-right: auto;
}

/* ── Method picker ────────────────────────────────────────────────────────── */
.mfa-method-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 4px;
}

.mfa-method-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: #E9ECEF;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  width: 100%;
}
.mfa-method-item:hover:not(:disabled) {
  border-color: #e5bc48;
  background: #fffbef;
  box-shadow: 0 1px 4px rgba(99,102,241,0.08);
}
.mfa-method-item.is-current {
  border-color: #2271a5;
  background: #f0f9ff;
}
.mfa-method-item:disabled {
  opacity: 0.6;
  cursor: default;
}

.mfa-method-icon-wrap {
  color: #172530;
  flex-shrink: 0;
  display: flex;
  font-size: 24px;
  font-weight: 400;
  min-width: 32px;
  min-height: 32px;
}
.mfa-method-item.is-current .mfa-method-icon-wrap { color: #2271a5; }

.mfa-activate-body {
  display: flex;
  color: #161F28;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  gap: 16px;
  margin-bottom: 12px;
}

.mfa-activate-body i {
  color: #2271A5;
  font-size: 48px;
  min-height: 56px;
  min-width: 56px;
  flex-shrink: 0;
  text-align: center;
  align-content: center;
}

.mfa-activate-body a {
  color: #2271A5;
  text-decoration: underline !important;
}

.mfa-qr-image {
  align-self: center;
  margin-bottom: 12px;

  img {
    width: 200px;
    height: 200px;
  }
}

.qr-error {
  width: 200px;
  height: 200px;
  border-radius: 8px;
  background: #E9ECEF;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #707070;
  font-size: 24px;
}

.qr-skeleton {
  width: 200px;
  height: 200px;
  border-radius: 8px;
  background: linear-gradient(90deg, #E9ECEF 25%, #f3f4f6 50%, #E9ECEF 75%);
  background-size: 400% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.mfa-method-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.mfa-method-name {
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  color: #172530;
}

.mfa-method-dest {
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #707070;
  letter-spacing: 0;
}

.mfa-method-security {
  font-size: 16px;
  font-weight: 400;
  color: #707070;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  white-space: nowrap;
}

.mfa-method-security i {
  font-size: 14px;
  min-width: 24px;
  min-height: 24px;
  align-content: center;
  text-align: center;
}

/* ── Backup List ────────────────────────────────────────────────────────── */
.mfa-backup-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  text-align: center;
  background-color: #E9ECEF;
  border-radius: 8px;
  padding: 24px 0;
}

.copy-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  font-size: 16px;
  min-height: 24px;
  min-width: 24px;
}

.codes-wrapper {
  position: relative;
}

.copy-tooltip {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #659157;
  color: #fff;
  font-size: 16px;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
}

.mfa-view-enter-active { transition: opacity .18s ease, transform .18s ease; }
.mfa-view-leave-active { transition: opacity .12s ease, transform .12s ease; }
.mfa-view-enter { opacity: 0; transform: translateX(12px); }
.mfa-view-leave-to { opacity: 0; transform: translateX(-12px); }

.tooltip-enter-active { transition: opacity 0.2s ease; }
.tooltip-leave-active { transition: opacity 0.4s ease; }
.tooltip-enter,
.tooltip-leave-to { opacity: 0; }

.mfa-footer-enter-active { transition: opacity .18s ease; }
.mfa-footer-leave-active { transition: opacity .12s ease; }
.mfa-footer-enter { opacity: 0; }
.mfa-footer-leave-to { opacity: 0; }

.mfa-error-fade-enter-active,
.mfa-error-fade-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.mfa-error-fade-enter,
.mfa-error-fade-leave-to { opacity: 0; transform: translateY(-4px); }

</style>